import { KeyRound, Loader2, Search } from 'lucide-react'
import type { OpenPermitListItem, PermitEmployeeLookup } from 'modules/permit/api/permitService'
import { Button } from 'shared/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'shared/ui/dialog'
import { Input } from 'shared/ui/input'
import { Label } from 'shared/ui/label'
import type { SupervisorSelection } from './types'

interface OpenPermitOpenDialogProps {
  open: boolean
  permitIds: string[]
  permits: OpenPermitListItem[]
  employeeSearchTerm: string
  employeeResults: PermitEmployeeLookup[]
  isEmployeeSearchLoading: boolean
  supervisors: SupervisorSelection[]
  isSubmitting: boolean
  onOpenChange: (open: boolean) => void
  onEmployeeSearchTermChange: (value: string) => void
  onAddSupervisor: (employee: PermitEmployeeLookup) => void
  onRemoveSupervisor: (index: number) => void
  onSubmit: () => void
}

export function OpenPermitOpenDialog({
  open,
  permitIds,
  permits,
  employeeSearchTerm,
  employeeResults,
  isEmployeeSearchLoading,
  supervisors,
  isSubmitting,
  onOpenChange,
  onEmployeeSearchTermChange,
  onAddSupervisor,
  onRemoveSupervisor,
  onSubmit,
}: OpenPermitOpenDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-emerald-600" />{permitIds.length > 1 ? 'Mass Open Permit' : 'เปิด Permit เข้าทำงาน'}</DialogTitle>
          <DialogDescription>{permitIds.length > 1 ? `กำหนดผู้ปฏิบัติงานก่อนเปิด Permit จำนวน ${permitIds.length} รายการ` : 'กำหนดผู้ปฏิบัติงานและเวลาปฏิบัติงานก่อนเปิด Permit'}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
            <p className="font-medium text-slate-900">รายการที่เลือก {permitIds.length} รายการ</p>
            <ul className="mt-2 space-y-1">
              {permits.slice(0, 5).map((permit) => <li key={permit.permitId}>{permit.permitNumber || permit.permitId} - {permit.workDescription || permit.title || '-'}</li>)}
              {permits.length > 5 ? <li>และอีก {permits.length - 5} รายการ</li> : null}
            </ul>
          </div>

          <div className="space-y-2">
            <Label>มอบหมายผู้ตรวจสอบหน้างาน (Operator)</Label>
            <div className="relative">
              <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <Input className="pl-9" value={employeeSearchTerm} onChange={(event) => onEmployeeSearchTermChange(event.target.value)} placeholder="พิมพ์ชื่อหรือรหัสพนักงานอย่างน้อย 2 ตัวอักษร" />
            </div>
            {employeeSearchTerm.trim().length >= 2 ? (
              <div className="max-h-52 overflow-y-auto rounded-lg border">
                {isEmployeeSearchLoading ? (
                  <div className="flex items-center gap-2 p-3 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />กำลังค้นหา...</div>
                ) : employeeResults.length === 0 ? (
                  <div className="p-3 text-sm text-slate-500">ไม่พบข้อมูลผู้ปฏิบัติงาน</div>
                ) : employeeResults.map((employee) => (
                  <div key={employee.employeeId} className="flex items-center justify-between border-b px-3 py-3 text-sm last:border-b-0">
                    <div>
                      <p className="font-medium text-slate-900">{employee.employeeName}</p>
                      <p className="text-slate-500">{employee.position || '-'} | {employee.department || '-'}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => onAddSupervisor(employee)}>เพิ่ม</Button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>รายชื่อ ({supervisors.length} คน)</Label>
              {supervisors.length === 0 ? <span className="text-xs text-red-500">ต้องเลือกอย่างน้อย 1 คน</span> : null}
            </div>

            {supervisors.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-slate-500">ยังไม่ได้เลือกผู้ปฏิบัติงาน</div>
            ) : (
              <div className="space-y-3">
                {supervisors.map((supervisor, index) => (
                  <div key={supervisor.employeeId} className="rounded-lg border p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-slate-900">{supervisor.employeeName}</p>
                        <p className="text-sm text-slate-500">{supervisor.position || '-'} | {supervisor.department || '-'}</p>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => onRemoveSupervisor(index)}>ลบ</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>ปิด</Button>
          <Button className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={onSubmit} disabled={isSubmitting || supervisors.length === 0}>{isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}ยืนยันเปิด Permit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
